import { useEffect, useMemo, useState } from 'react'
import { authedAxios } from '../../utils'
import { getDateForInputFieldFromTimestamp } from '../../utils/date'
import { updateObjectWithMatchingId } from '../../utils/array'

const useCustomerOpportunities = (
  customerId,
  { isNewOpportunity, isNewCustomer, pipelines } = {}
) => {
  const url = `/api/customer/${customerId}/opportunity`

  const [customerOpportunities, setCustomerOpportunities] = useState([])
  const [customerOpportunitiesCopy, setCustomerOpportunitiesCopy] = useState([])

  const firstStageId =
    pipelines
      ?.find((p) => p.id === '__OPPORTUNITIES__')
      ?.stages.find((s) => s.displayOrder === 1)?.id || 1

  const NEW_OPPORTUNITY = useMemo(
    () => ({
      isOpen: true,
      statusTypeId: 1,
      products: [],
      potentialOneTimeRevenue: 0,
      potentialRecurringRevenue: 0,
      potentialBillingCycleTypeId: 1,
      daysToClose: '',
      percentToClose: 0,
      startDate: new Date().toLocaleDateString('sv'),
      stageId: firstStageId,
    }),
    [firstStageId]
  )

  const currentOpportunity = customerOpportunities.find((o) => o?.isOpen)

  const [isLoading, setIsLoading] = useState(true)
  const [hasLoadError, setHasLoadError] = useState(false)

  const getCustomerOpportunities = () => {
    if (isNewCustomer) {
      setCustomerOpportunities([])
      setIsLoading(false)
      return Promise.resolve()
    }

    return authedAxios
      .get(url)
      .then((res) => {
        const normalized = res.data.customerOpportunities.map((o) => ({
          ...o,
          // MAGIC_NUMBER: status type 1 === open
          isOpen: o.statusTypeId === 1,
          products: o.products.map((p) => ({
            ...p,
            startDate: getDateForInputFieldFromTimestamp(p.startDate),
            endDate: getDateForInputFieldFromTimestamp(p.endDate),
          })),
        }))

        setCustomerOpportunities(normalized)
        setCustomerOpportunitiesCopy(normalized)
        setIsLoading(false)
      })
      .catch((e) => {
        setHasLoadError(e)
      })
  }

  const setCurrentOpportunity = (newValue) => {
    const index = customerOpportunities.indexOf(currentOpportunity)

    setCustomerOpportunities([
      ...customerOpportunities.slice(0, index),
      newValue,
      ...customerOpportunities.slice(index + 1),
    ])
  }

  const saveCurrentOpportunity = (inId) => {
    const id = inId || customerId
    if (!id) return

    const updateUrl = `/api/customer/${id}/opportunity`

    return isNewOpportunity
      ? authedAxios.post(updateUrl, currentOpportunity)
      : authedAxios.put(
          `${updateUrl}/${currentOpportunity.id}`,
          currentOpportunity
        )
  }

  const saveOpportunity = (inCId, inOId, val) => {
    const cId = inCId || customerId

    if (!cId) return

    const updateUrl = `/api/customer/${cId}/opportunity/${inOId}`

    return authedAxios.put(`${updateUrl}`, val)
  }

  const reset = () => {
    setCustomerOpportunities(customerOpportunitiesCopy)
  }

  const closeOpportunity = (args) => {
    return authedAxios.put(`${url}/${currentOpportunity.id}/close`, args)
  }

  const deleteOpportunity = (inId) => {
    return authedAxios
      .delete(`${url}/${inId || currentOpportunity.id}`)
      .then(getCustomerOpportunities)
  }

  const updateOpportunityWithId = (id, val) => {
    setCustomerOpportunities(
      updateObjectWithMatchingId(customerOpportunities, val)
    )
  }

  useEffect(() => {
    if (!customerId) return
    getCustomerOpportunities()
  }, [customerId])

  useEffect(() => {
    if (isNewOpportunity) {
      return setCustomerOpportunities([
        NEW_OPPORTUNITY,
        ...customerOpportunitiesCopy,
      ])
    } else {
      setCustomerOpportunities(customerOpportunitiesCopy)
    }
  }, [isNewOpportunity])

  return {
    customerOpportunities,
    isLoading,
    hasLoadError,
    currentOpportunity,
    setCurrentOpportunity,
    saveCurrentOpportunity,
    reset,
    closeOpportunity,
    deleteOpportunity,
    getCustomerOpportunities,
    updateOpportunityWithId,
    saveOpportunity,
  }
}

export default useCustomerOpportunities
